'use client';

import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Toast — Radix Toast wrapper with Hauraki tokens.
 *
 * Variants: default | success | warn | error | info.
 *
 * Usage:
 *   1. Wrap your app in `<ToastProvider position="top-right">`.
 *   2. Call the hook: `const { toast } = useToast()`, then
 *      `toast({ title, description, variant, duration })`.
 *   3. The provider mounts the viewport + rendered toasts automatically.
 */

type ToastVariant = "default" | "success" | "warn" | "error" | "info";
type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center";

const toastVariants = cva(
  [
    "group pointer-events-auto relative flex w-full items-start gap-space-12",
    "overflow-hidden rounded-card border shadow-lg",
    "p-space-16 pe-space-32",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[swipe=end]:animate-out",
    "data-[state=closed]:fade-out-80 data-[state=open]:fade-in-0",
    "data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-chrome-surface border-chrome-border text-ink-primary",
        success:
          "bg-chrome-surface border-status-success text-ink-primary [&_[data-slot=toast-icon]]:text-status-success",
        warn:
          "bg-chrome-surface border-status-warn text-ink-primary [&_[data-slot=toast-icon]]:text-status-warn",
        error:
          "bg-chrome-surface border-status-error text-ink-primary [&_[data-slot=toast-icon]]:text-status-error",
        info:
          "bg-chrome-surface border-accent-violet text-ink-primary [&_[data-slot=toast-icon]]:text-accent-violet",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const variantIcon: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  default: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

const positionClasses: Record<ToastPosition, string> = {
  "top-right": "top-0 end-0 flex-col",
  "top-left": "top-0 start-0 flex-col",
  "top-center": "top-0 left-1/2 -translate-x-1/2 flex-col items-center",
  "bottom-right": "bottom-0 end-0 flex-col-reverse",
  "bottom-left": "bottom-0 start-0 flex-col-reverse",
  "bottom-center":
    "bottom-0 left-1/2 -translate-x-1/2 flex-col-reverse items-center",
};

// ---- Radix wrappers (low-level) ---------------------------------------------

export const ToastProviderPrimitive = ToastPrimitive.Provider;

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport> & {
    position?: ToastPosition;
  }
>(function ToastViewport({ className, position = "top-right", ...props }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      data-slot="toast-viewport"
      data-position={position}
      className={cn(
        "fixed z-[100] flex max-h-screen w-full max-w-[420px] gap-space-12 p-space-24",
        "outline-none",
        positionClasses[position],
        className,
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = "ToastViewport";

export interface ToastRootProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const ToastRoot = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastRootProps
>(function ToastRoot({ className, variant, ...props }, ref) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      data-slot="toast-root"
      className={cn(toastVariants({ variant, className }))}
      {...props}
    />
  );
});
ToastRoot.displayName = "ToastRoot";

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(function ToastTitle({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Title
      ref={ref}
      className={cn("text-body-14 font-semibold", className)}
      {...props}
    />
  );
});
ToastTitle.displayName = "ToastTitle";

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(function ToastDescription({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cn("text-body-14 text-ink-muted", className)}
      {...props}
    />
  );
});
ToastDescription.displayName = "ToastDescription";

export const ToastCloseButton = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(function ToastCloseButton({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Close
      ref={ref}
      aria-label="Close"
      className={cn(
        "absolute end-space-8 top-space-8 rounded-chip p-1",
        "text-ink-muted hover:text-ink-primary",
        "outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
        className,
      )}
      {...props}
    >
      <X className="size-4" />
    </ToastPrimitive.Close>
  );
});
ToastCloseButton.displayName = "ToastCloseButton";

// ---- Provider + hook (high-level API) ---------------------------------------

interface ToastEntry {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastContextValue {
  toast: (input: Omit<ToastEntry, "id"> & { id?: string }) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

let toastIdCounter = 0;
function nextToastId() {
  toastIdCounter += 1;
  return `toast-${toastIdCounter}`;
}

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  /** Default auto-dismiss timeout in ms. Individual toasts can override. */
  defaultDuration?: number;
  /** Max number of visible toasts before older ones drop. */
  swipeDirection?: "right" | "left" | "up" | "down";
}

export function ToastProvider({
  children,
  position = "top-right",
  defaultDuration = 5000,
  swipeDirection = "right",
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastEntry[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback<ToastContextValue["toast"]>((input) => {
    const id = input.id ?? nextToastId();
    setToasts((prev) => [
      ...prev,
      { id, variant: "default", ...input },
    ]);
    return id;
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({ toast, dismiss }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastProviderPrimitive swipeDirection={swipeDirection}>
        {children}
        {toasts.map((t) => {
          const Icon = variantIcon[t.variant ?? "default"];
          return (
            <ToastRoot
              key={t.id}
              variant={t.variant}
              duration={t.duration ?? defaultDuration}
              onOpenChange={(open) => {
                if (!open) dismiss(t.id);
              }}
            >
              <span
                data-slot="toast-icon"
                className="mt-0.5 shrink-0 text-ink-muted"
                aria-hidden="true"
              >
                <Icon className="size-4" />
              </span>
              <div className="flex-1 min-w-0 flex flex-col gap-space-4">
                {t.title && <ToastTitle>{t.title}</ToastTitle>}
                {t.description && (
                  <ToastDescription>{t.description}</ToastDescription>
                )}
              </div>
              <ToastCloseButton />
            </ToastRoot>
          );
        })}
        <ToastViewport position={position} />
      </ToastProviderPrimitive>
    </ToastContext.Provider>
  );
}

/** Read the toast controls; must be called inside `<ToastProvider>`. */
export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

export { toastVariants };
